import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import CommentForm from "../components/CommentForm";
import CommentList from "../components/CommentList";


type Thread = {
  id: string;
  title: string;
  body: string | null;
  status: "open" | "planned" | "in_progress" | "done" | "wont_fix";
  created_at: string;
  author_initials: string;
  votes_count: number;
  user_voted: boolean;
};

const STATUS_LABEL: Record<Thread["status"], string> = {
  open: "Offen",
  planned: "Geplant",
  in_progress: "In Arbeit",
  done: "Erledigt",
  wont_fix: "Kein Fix",
};

const STATUS_CLASS: Record<Thread["status"], string> = {
  open: "bg-slate-700/30 text-slate-300 border-slate-600",
  planned: "bg-sky-900/30 text-sky-400 border-sky-800",
  in_progress: "bg-amber-900/30 text-amber-400 border-amber-800",
  done: "bg-emerald-900/30 text-emerald-400 border-emerald-800",
  wont_fix: "bg-red-900/30 text-red-400 border-red-800",
};

export default function FeedbackThread() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [thread, setThread] = useState<Thread | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isVoted = !!thread?.user_voted;

  const load = async () => {
    if (!threadId) return;
    setLoading(true);
    setError(null);

    const { data, error } = await supabase.rpc("feedback_thread_by_id", {
      p_id: threadId,
    });

    if (error) {
      console.error("feedback_thread_by_id failed:", error.message);
      setError("Konnte Feedback nicht laden.");
      setThread(null);
      setLoading(false);
      return;
    }

    if (!data) {
      setError("Feedback nicht gefunden.");
      setThread(null);
      setLoading(false);
      return;
    }

    // RPC gibt ein einzelnes Row-Objekt zurück; supabase-js liefert es ggf. als Array.
    const row = Array.isArray(data) ? data[0] : data;
    setThread(row as Thread);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, user?.id]);

  const toggleVote = async () => {
    if (!user || !thread) {
      alert("Bitte logge dich ein, um abzustimmen.");
      return;
    }
    // Optimistic UI
    setThread((prev) =>
      prev
        ? {
            ...prev,
            votes_count: prev.user_voted ? prev.votes_count - 1 : prev.votes_count + 1,
            user_voted: !prev.user_voted,
          }
        : prev
    );

    const { error } = await supabase.rpc("feedback_toggle_vote", {
      p_thread_id: thread.id,
    });
    if (error) {
      console.error("toggle_vote failed:", error.message);
      // Rollback
      setThread((prev) =>
        prev
          ? {
              ...prev,
              votes_count: !prev.user_voted ? prev.votes_count - 1 : prev.votes_count + 1,
              user_voted: !prev.user_voted,
            }
          : prev
      );
      alert("Konnte deine Stimme nicht speichern.");
    }
  };

  // Admin-Check via Profile-Role
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.id) {
        setIsAdmin(false);
        return;
      }
      const { data, error } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .maybeSingle();
      if (!alive) return;
      if (error || !data) {
        setIsAdmin(false);
      } else {
        setIsAdmin(data.role === "admin");
      }
    })();
    return () => {
      alive = false;
    };
  }, [user?.id]);

  const onChangeStatus = async (next: Thread["status"]) => {
    if (!thread) return;
    setSavingStatus(true);
    setError(null);

    // Optimistic UI
    const prev = thread.status;
    setThread({ ...thread, status: next });

    const { error } = await supabase
      .from("feedback_threads")
      .update({ status: next })
      .eq("id", thread.id);

    if (error) {
      console.error("status update failed:", error.message);
      setError("Status konnte nicht gespeichert werden.");
      setThread((t) => (t ? { ...t, status: prev } : t));
    }
    setSavingStatus(false);
  };

  const disableVoteHint = useMemo(
    () => (!user ? "Bitte einloggen, um abzustimmen." : ""),
    [user]
  );

  if (loading)
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center text-slate-500">
        ⏳ Lade Feedback…
      </div>
    );

  if (error || !thread)
    return (
      <div className="min-h-screen bg-[#0d1117]">
        <div className="max-w-4xl mx-auto pt-20 pb-20 px-4 sm:px-6">
          <p className="text-red-400">{error ?? "Unbekannter Fehler."}</p>
          <div className="mt-4">
            <button
              className="text-sky-400 hover:underline"
              onClick={() => navigate("/feedback")}
            >
              ← Zur Übersicht
            </button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-4xl mx-auto pt-20 pb-20 px-4 sm:px-6">
        <div className="flex items-start gap-4">
          {/* Vote */}
          <button
            className={`min-w-16 flex flex-col items-center justify-center rounded-lg border px-3 py-2 ${
              isVoted ? "bg-sky-900/40 border-sky-400" : "bg-[#1e2639] border-[#2a3347]"
            } ${!user ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
            onClick={() => (user ? toggleVote() : undefined)}
            title={disableVoteHint || (isVoted ? "Abstimmung zurücknehmen" : "Ich will das!")}
          >
            <span className="text-lg">👍</span>
            <span className="text-sm font-semibold tabular-nums text-slate-200">{thread.votes_count}</span>
          </button>

          {/* Inhalt */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs px-2 py-1 rounded border ${STATUS_CLASS[thread.status]}`}>
                {STATUS_LABEL[thread.status]}
              </span>
              <span className="text-xs text-slate-500">
                {new Date(thread.created_at).toLocaleDateString()}
              </span>
              <span className="text-xs text-slate-500">•</span>
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#334155] text-white text-xs">
                {thread.author_initials}
              </span>

              <span className="flex-1" />

              {/* Admin: Status ändern */}
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-500">Status:</label>
                  <select
                    className="border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded px-2 py-1 text-sm"
                    value={thread.status}
                    disabled={savingStatus}
                    onChange={(e) => onChangeStatus(e.target.value as Thread["status"])}
                  >
                    <option value="open">Offen</option>
                    <option value="planned">Geplant</option>
                    <option value="in_progress">In Arbeit</option>
                    <option value="done">Erledigt</option>
                    <option value="wont_fix">Kein Fix</option>
                  </select>
                </div>
              )}
            </div>

            <h1 className="text-2xl font-bold text-slate-100">{thread.title}</h1>

            {thread.body && (
              <div className="prose prose-invert mt-4 max-w-none">
                <p className="whitespace-pre-wrap text-slate-300">{thread.body}</p>
              </div>
            )}

            <div className="mt-6">
              <Link to="/feedback" className="text-sky-400 hover:underline text-sm">
                ← Zur Übersicht
              </Link>
            </div>
          </div>
        </div>

        {/* 💬 Kommentare */}
        <div className="mt-10 border-t border-[#2a3347] pt-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-100">💬 Kommentare</h2>

          {/* Neues Kommentarformular */}
          {user ? (
            <CommentForm
              threadId={thread.id}
              onAdded={load}
            />
          ) : (
            <p className="text-slate-400 mb-4">
              Bitte logge dich ein, um einen Kommentar zu schreiben.
            </p>
          )}

          {/* Kommentar-Liste */}
          <CommentList
            threadId={thread.id}
            currentUserId={user?.id ?? null}
          />
        </div>
      </div>
    </div>
  );
}
